import { useState, useEffect } from 'react'
import apiClient from '@services/api/client'
import { toast } from 'react-hot-toast'
import AdminLayout from '../../../layouts/AdminLayout'

const LEVEL_CFG = {
  info:    { bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500'   },
  warning: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  error:   { bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500'    },
  debug:   { bg: 'bg-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-400'   },
}
const getLvl = (l) => LEVEL_CFG[l] || { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' }

function SecurityReportsPage() {
  const [loading,          setLoading         ] = useState(true)
  const [dateRange,        setDateRange       ] = useState('7days')
  const [stats,            setStats           ] = useState({ total: 0, by_level: {}, by_type: {} })
  const [recentLogs,       setRecentLogs      ] = useState([])
  const [topTypes,         setTopTypes        ] = useState([])

  useEffect(() => { loadData() }, [dateRange])

  const loadData = async () => {
    setLoading(true)
    try {
      const [statsRes, logsRes] = await Promise.all([
        apiClient.get('/logs/stats'),
        apiClient.get('/logs', { params: { page: 1 } }),
      ])

      if (statsRes?.success !== false) {
        const d = statsRes.data || {}
        setStats(d)

        // Top types triés par count
        const types = Object.entries(d.by_type || {})
          .map(([type, count]) => ({ type, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 6)
        setTopTypes(types)
      }

      if (logsRes?.success !== false) {
        setRecentLogs((logsRes.data || []).slice(0, 10))
      }
    } catch (error) {
      console.error('Erreur chargement rapports:', error)
      toast.error('Erreur lors du chargement des rapports')
    } finally {
      setLoading(false)
    }
  }

  const exportReport = () => {
    toast.success('Exportation en cours…')
  }

  const totalLogins  = stats.by_type?.user_login   || 0
  const totalLogouts = stats.by_type?.user_logout  || 0
  const totalErrors  = stats.by_level?.error        || 0
  const totalWarnings= stats.by_level?.warning      || 0
  const totalInfo    = stats.by_level?.info         || 0
  const totalPosts   = (stats.by_type?.post_created || 0) + (stats.by_type?.post_updated || 0)

  const statCards = [
    { label: 'Total logs',       value: stats.total ?? 0, sub: 'Toutes entrées',       iconBg: 'bg-blue-50',   iconColor: 'text-blue-600',   numColor: 'text-gray-800',   d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { label: 'Connexions',       value: totalLogins,      sub: 'user_login',            iconBg: 'bg-green-50',  iconColor: 'text-green-600',  numColor: 'text-green-600',  d: 'M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1' },
    { label: 'Déconnexions',     value: totalLogouts,     sub: 'user_logout',           iconBg: 'bg-orange-50', iconColor: 'text-orange-600', numColor: 'text-orange-600', d: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1' },
    { label: 'Erreurs',          value: totalErrors,      sub: 'level: error',          iconBg: 'bg-red-50',    iconColor: 'text-red-600',    numColor: 'text-red-600',    d: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' },
    { label: 'Avertissements',   value: totalWarnings,    sub: 'level: warning',        iconBg: 'bg-yellow-50', iconColor: 'text-yellow-600', numColor: 'text-yellow-600', d: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
    { label: 'Publications',     value: totalPosts,       sub: 'créées + modifiées',    iconBg: 'bg-purple-50', iconColor: 'text-purple-600', numColor: 'text-purple-600', d: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
    { label: 'Info',             value: totalInfo,        sub: 'level: info',           iconBg: 'bg-blue-50',   iconColor: 'text-blue-600',   numColor: 'text-blue-600',   d: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { label: 'Utilisateurs',     value: stats.by_type?.user_created || 0, sub: 'nouveaux inscrits', iconBg: 'bg-cyan-50', iconColor: 'text-cyan-600', numColor: 'text-cyan-600', d: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z' },
  ]

  const successPct = stats.total > 0
    ? Math.round(((stats.total - totalErrors) / stats.total) * 100)
    : 0

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">

        {/* ── Header ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">Rapports de Sécurité</h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Analyse et statistiques de sécurité — données en temps réel
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadData}
                className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all text-gray-700 font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Actualiser
              </button>
              <button
                onClick={exportReport}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] text-white rounded-lg hover:shadow-lg transition-all font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Exporter
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
              <p className="text-gray-400 text-sm">Chargement des rapports…</p>
            </div>
          </div>
        ) : (
          <>
            {/* ── Barre de santé globale ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-800">Santé du système</h3>
                  <p className="text-sm text-gray-500 mt-0.5">{stats.total ?? 0} événements enregistrés</p>
                </div>
                <span className={`text-2xl font-bold ${successPct >= 90 ? 'text-green-600' : successPct >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {successPct}%
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className="flex h-full">
                  <div className="bg-green-500 transition-all duration-700" style={{ width: `${successPct}%` }} />
                  <div className="bg-red-400 transition-all duration-700" style={{ width: `${100 - successPct}%` }} />
                </div>
              </div>
              <div className="flex items-center gap-6 mt-3 text-xs text-gray-500">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block"/>{stats.total - totalErrors} succès</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block"/>{totalErrors} erreur{totalErrors > 1 ? 's' : ''}</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block"/>{totalWarnings} avertissement{totalWarnings > 1 ? 's' : ''}</span>
              </div>
            </div>

            {/* ── Stats cards ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {statCards.map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-medium mb-1">{s.label}</p>
                      <p className={`text-2xl font-bold ${s.numColor}`}>{s.value}</p>
                      <p className="text-xs text-gray-400 mt-1">{s.sub}</p>
                    </div>
                    <div className={`w-12 h-12 ${s.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <svg className={`w-6 h-6 ${s.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={s.d} />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Bottom grid ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Top types d'activités */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-800">Répartition par type</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Types d'événements les plus fréquents</p>
                </div>
                <div className="p-6">
                  {topTypes.length > 0 ? (
                    <div className="space-y-4">
                      {topTypes.map(({ type, count }, i) => {
                        const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0
                        const colors = ['bg-blue-500','bg-green-500','bg-yellow-500','bg-purple-500','bg-orange-500','bg-red-500']
                        return (
                          <div key={type}>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-sm font-medium text-gray-700 capitalize">{type.replace(/_/g, ' ')}</span>
                              <span className="text-sm font-bold text-gray-800">{count} <span className="text-xs text-gray-400 font-normal">({pct}%)</span></span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-700 ${colors[i % colors.length]}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <p className="text-sm">Aucune donnée disponible</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Activités récentes */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-800">Activités récentes</h2>
                  <p className="text-xs text-gray-500 mt-0.5">10 derniers événements système</p>
                </div>
                <div className="p-4">
                  {recentLogs.length > 0 ? (
                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                      {recentLogs.map((log, i) => {
                        const lvl = getLvl(log.level)
                        const userName = log.user
                          ? [log.user.prenom, log.user.nom].filter(Boolean).join(' ') || log.user.email || ''
                          : ''
                        const time = log.created_at
                          ? new Date(log.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                          : ''
                        const date = log.created_at
                          ? new Date(log.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                          : ''
                        return (
                          <div key={log.id ?? i} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${lvl.dot}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{log.message || '—'}</p>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${lvl.bg} ${lvl.text}`}>
                                  {log.level || 'info'}
                                </span>
                                {log.type && (
                                  <span className="text-xs text-gray-400">{log.type.replace(/_/g, ' ')}</span>
                                )}
                                {userName && (
                                  <span className="text-xs text-gray-400 truncate">• {userName}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <p className="text-xs font-mono text-gray-500">{time}</p>
                              <p className="text-xs text-gray-400">{date}</p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <p className="text-sm">Aucune activité récente</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  )
}

export default SecurityReportsPage
